import { MONTHS_RUS, monthsOrder } from "@shared/constants";

import type { EventType } from "@/_pages/event/model";

interface MonthlyGroup {
  month: string;
  events: EventType[];
}

interface YearlyGroup {
  year: string;
  months: MonthlyGroup[];
}

const sortedEvents = (a: EventType, b: EventType) => {
  const timestampA = new Date(`${a.date}T${a.time}`).getTime();

  const timestampB = new Date(`${b.date}T${b.time}`).getTime();

  return timestampA - timestampB;
};

const toStartOfDay = (date: Date) => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);

  return normalizedDate;
};

export const rebuildEvents = (events: EventType[]) => {
  const yearlyGroups = new Map<string, Map<string, EventType[]>>();
  const today = toStartOfDay(new Date());

  for (const event of events) {
    const startDate = toStartOfDay(new Date(event.date));
    const endDate = toStartOfDay(new Date(event.dateEnd ?? event.date));

    // Если дата окончания (или дата события) в прошлом, событие не показываем.
    if (endDate < today) {
      continue;
    }

    // Для длительных событий, которые начались раньше, вывод начинаем с текущего месяца.
    const loopStartDate = startDate < today ? new Date(today) : new Date(startDate);

    // Получаем все месяцы между date и dateEnd
    const currentDate = new Date(loopStartDate);
    currentDate.setDate(1); // Устанавливаем первое число месяца для корректного сравнения

    const endDateForLoop = new Date(endDate);
    endDateForLoop.setDate(1);

    while (currentDate <= endDateForLoop) {
      const yearKey = currentDate.getFullYear().toString();
      const monthIndex = currentDate.getMonth(); // 0-11
      const monthKey = MONTHS_RUS.base[monthIndex];

      if (!yearlyGroups.has(yearKey)) {
        yearlyGroups.set(yearKey, new Map<string, EventType[]>());
      }

      const currentYearMap = yearlyGroups.get(yearKey)!;

      if (currentYearMap.has(monthKey)) {
        currentYearMap.get(monthKey)!.push(event);
      } else {
        currentYearMap.set(monthKey, [event]);
      }

      // Переходим к следующему месяцу
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
  }

  const result: YearlyGroup[] = [];

  for (const [year, monthlyMap] of yearlyGroups.entries()) {
    const monthlyGroups: MonthlyGroup[] = Array.from(monthlyMap.entries())
      .map(([month, events]) => ({
        month,
        events: events.sort(sortedEvents),
      }))
      .sort(
        (a, b) =>
          monthsOrder[a.month as keyof typeof monthsOrder] -
          monthsOrder[b.month as keyof typeof monthsOrder],
      );

    result.push({
      year,
      months: monthlyGroups,
    });
  }

  return result.sort((a, b) => {
    const yearA = parseInt(a.year, 10);
    const yearB = parseInt(b.year, 10);

    return yearA - yearB;
  });
};
